/**
 * Utility functions for handling avatar URLs with SAS tokens
 */

/**
 * Appends the SAS token from environment variable to an avatar URL
 * @param avatarUrl - The base avatar URL
 * @returns The avatar URL with SAS token appended, or empty string if no URL provided
 */
export function getAvatarUrlWithSas(avatarUrl?: string | null, cacheKey?: string): string {
  if (!avatarUrl) return '';

  const sas = process.env.REACT_APP_AVATAR_SAS;

  // Prefer robust URL handling; fall back to string ops for relative URLs
  try {
    const url = new URL(avatarUrl);

    if (sas) {
      const clean = sas.startsWith('?') ? sas.slice(1) : sas;
      const sasParams = new URLSearchParams(clean);
      sasParams.forEach((value, key) => url.searchParams.set(key, value));
    }

    if (cacheKey) {
      url.searchParams.set('v', cacheKey);
    }

    return url.toString();
  } catch {
    // Non-absolute URL (e.g., relative path). Append SAS and cache key via string operations
    const segments: string[] = [];
    const existingQuery = avatarUrl.includes('?') ? avatarUrl.split('?')[1] : '';
    if (existingQuery) segments.push(existingQuery);
    if (sas) segments.push(sas.startsWith('?') ? sas.slice(1) : sas);
    if (cacheKey) segments.push(`v=${encodeURIComponent(cacheKey)}`);
    const query = segments.filter(Boolean).join('&');
    const base = avatarUrl.split('?')[0];
    return query ? `${base}?${query}` : base;
  }
}
